/**
 * Cell status legend component
 * Legend showing cell status colors and counts
 */

import { Space, Typography, Badge, Tooltip } from 'antd';
import type { GridCellStatus } from '@/types/grid';
import { CELL_STATUS_CONFIG, CELL_STATUS_COLORS } from '@/utils/constants';
import { formatNumber } from '@/utils/formatters';

const { Text } = Typography;

interface CellStatusCounts {
  pending: number;
  queued: number;
  processing: number;
  completed: number;
  failed: number;
  skipped: number;
}

interface CellStatusLegendProps {
  counts?: Partial<CellStatusCounts>;
  showCounts?: boolean;
  direction?: 'horizontal' | 'vertical';
  size?: 'small' | 'default';
}

export function CellStatusLegend({
  counts,
  showCounts = true,
  direction = 'horizontal',
  size = 'default',
}: CellStatusLegendProps) {
  const statuses: GridCellStatus[] = ['pending', 'queued', 'processing', 'completed', 'failed', 'skipped'];

  return (
    <Space
      direction={direction}
      size={direction === 'horizontal' ? 16 : 8}
      wrap
    >
      {statuses.map((status) => {
        const config = CELL_STATUS_CONFIG[status];
        const count = counts?.[status] ?? 0;

        return (
          <Tooltip key={status} title={config.label}>
            <Space size={4} align="center">
              <div
                style={{
                  width: size === 'small' ? 12 : 16,
                  height: size === 'small' ? 12 : 16,
                  backgroundColor: CELL_STATUS_COLORS[status],
                  borderRadius: 3,
                  opacity: count === 0 && showCounts ? 0.4 : 1,
                }}
              />
              <Text
                style={{
                  fontSize: size === 'small' ? 12 : 14,
                  color: count === 0 && showCounts ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)',
                }}
              >
                {config.label}
                {showCounts && ` (${formatNumber(count)})`}
              </Text>
            </Space>
          </Tooltip>
        );
      })}
    </Space>
  );
}

/**
 * Compact legend with just dots
 */
export function CellStatusLegendCompact({
  counts,
}: {
  counts?: Partial<CellStatusCounts>;
}) {
  const statuses: GridCellStatus[] = ['completed', 'failed', 'processing', 'queued', 'pending'];

  return (
    <Space size={8}>
      {statuses.map((status) => {
        const config = CELL_STATUS_CONFIG[status];
        const count = counts?.[status] ?? 0;

        if (count === 0) return null;

        return (
          <Tooltip key={status} title={`${config.label}: ${formatNumber(count)}`}>
            <Badge
              count={count}
              color={CELL_STATUS_COLORS[status]}
              size="small"
              overflowCount={9999}
            />
          </Tooltip>
        );
      })}
    </Space>
  );
}

/**
 * Progress bar style legend
 */
export function CellStatusBar({
  counts,
  total,
}: {
  counts: Partial<CellStatusCounts>;
  total: number;
}) {
  if (total === 0) return null;

  const getWidth = (count: number) => `${(count / total) * 100}%`;

  return (
    <Tooltip
      title={
        <div>
          <div>Completed: {formatNumber(counts.completed ?? 0)}</div>
          <div>Failed: {formatNumber(counts.failed ?? 0)}</div>
          <div>Processing: {formatNumber(counts.processing ?? 0)}</div>
          <div>Queued: {formatNumber(counts.queued ?? 0)}</div>
          <div>Pending: {formatNumber(counts.pending ?? 0)}</div>
          <div>Skipped: {formatNumber(counts.skipped ?? 0)}</div>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          height: 8,
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: 'var(--color-bg-spotlight)',
        }}
      >
        {counts.completed && counts.completed > 0 && (
          <div
            style={{
              width: getWidth(counts.completed),
              backgroundColor: CELL_STATUS_COLORS.completed,
            }}
          />
        )}
        {counts.failed && counts.failed > 0 && (
          <div
            style={{
              width: getWidth(counts.failed),
              backgroundColor: CELL_STATUS_COLORS.failed,
            }}
          />
        )}
        {counts.processing && counts.processing > 0 && (
          <div
            style={{
              width: getWidth(counts.processing),
              backgroundColor: CELL_STATUS_COLORS.processing,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        )}
        {counts.queued && counts.queued > 0 && (
          <div
            style={{
              width: getWidth(counts.queued),
              backgroundColor: CELL_STATUS_COLORS.queued,
            }}
          />
        )}
        {counts.pending && counts.pending > 0 && (
          <div
            style={{
              width: getWidth(counts.pending),
              backgroundColor: CELL_STATUS_COLORS.pending,
            }}
          />
        )}
        {counts.skipped && counts.skipped > 0 && (
          <div
            style={{
              width: getWidth(counts.skipped),
              backgroundColor: CELL_STATUS_COLORS.skipped,
            }}
          />
        )}
      </div>
    </Tooltip>
  );
}

export default CellStatusLegend;
